import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { fetchWithAuth } from "../config/api";

const StaffPerformance = () => {
  const { access, refreshAccessToken, logout } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${m}`; // YYYY-MM
  });
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getStaffRevenue = (s) =>
    Number(s?.service_revenue ?? 0) + Number(s?.item_revenue ?? 0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth(
          `/api/staff/performance/?month=${encodeURIComponent(selectedMonth)}`
        );

        if (res.status === 401 || res.status === 403) {
          if (logout) logout();
          return;
        }

        if (!res.ok) {
          throw new Error(`Failed to load staff performance (${res.status})`);
        }

        const data = await res.json();
        setMetrics(data);

        if (!selectedStaffId) {
          setSelectedStaffId("");
        }
      } catch (e) {
        console.error("Failed to load staff performance", e);
        setError(e.message || "Failed to load staff performance");
      } finally {
        setLoading(false);
      }
    };

    load();
    // we *only* want to refetch when the month changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);


  const staffList = Array.isArray(metrics?.staff) ? metrics.staff : [];

  const shopTarget = Number(metrics?.shop_target ?? 0);
  const shopRevenue = Number(metrics?.shop_revenue ?? 0);
  const shopPercent =
    metrics?.shop_achievement_percent != null
      ? metrics.shop_achievement_percent
      : shopTarget > 0
      ? Math.round((shopRevenue / shopTarget) * 100)
      : null;

  const resolvedStaffId = selectedStaffId || "";
  const selectedStaff =
    staffList.find((s) => {
      const id =
        s.staff_id ?? s.id ?? s.user_id ?? s.pk ?? s.username ?? "";
      return String(id) === resolvedStaffId;
    }) || null;

  const selectedStaffRevenue = selectedStaff ? getStaffRevenue(selectedStaff) : 0;
  const selectedStaffTarget = selectedStaff
    ? Number(selectedStaff.monthly_target ?? 0)
    : 0;
  const selectedStaffPercent =
    selectedStaff && selectedStaffTarget > 0
      ? Math.round((selectedStaffRevenue / selectedStaffTarget) * 100)
      : null;
  const selectedStaffShareOfShop =
    selectedStaff && shopTarget > 0
      ? Math.round((selectedStaffRevenue / shopTarget) * 100)
      : null;

  return (
    <div className="card">
      <div
        className="card-header"
        style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}
      >
        <h3 style={{ margin: 0 }}>Staff Performance</h3>

        {/* Month selector */}
        <label style={{ fontWeight: "normal" }}>
          Month:{" "}
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </label>
      </div>

      {/* Staff selector */}
      <div
        style={{
          margin: "0.75rem 0 1rem",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <label>
          Staff:&nbsp;
          <select
            value={resolvedStaffId}
            onChange={(e) => setSelectedStaffId(e.target.value)}
          >
            <option value="">All staff</option>
            {staffList.map((s) => {
              const id =
                s.staff_id ?? s.id ?? s.user_id ?? s.pk ?? s.username ?? "";
              const name =
                s.display_name ||
                s.username ||
                s.name ||
                s.full_name ||
                `Staff ${s.staff_id ?? s.id ?? ""}`;
              return (
                <option key={id} value={String(id)}>
                  {name}
                </option>
              );
            })}
          </select>
        </label>
      </div>

      {loading && <div>Loading performance…</div>}
      {error && <div style={{ color: "red" }}>{error}</div>}

      {!loading && !error && metrics && (
        <>
          {/* Shop / manager target summary */}
          <div
            style={{
              marginBottom: "1.25rem",
              padding: "0.75rem 1rem",
              border: "1px solid #ddd",
              borderRadius: 4,
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Shop Performance (Manager Target)
            </h4>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <div>
                <strong>Shop Target:</strong>{" "}
                {shopTarget > 0 ? shopTarget.toFixed(2) : "—"}
              </div>
              <div>
                <strong>Shop Achieved:</strong> {shopRevenue.toFixed(2)}
              </div>
              <div>
                <strong>Progress:</strong>{" "}
                {shopPercent != null ? `${shopPercent}%` : "—"}
              </div>
            </div>
          </div>

          {/* Selected staff summary (optional) */}
          {selectedStaff && (
            <div
              style={{
                marginBottom: "1.25rem",
                padding: "0.75rem 1rem",
                border: "1px solid #eee",
                borderRadius: 4,
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                {selectedStaff.display_name ||
                selectedStaff.username ||
                  selectedStaff.name ||
                  selectedStaff.full_name ||
                  `Staff ${selectedStaff.staff_id ?? selectedStaff.id ?? ""}`}
              </h4>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div>
                  <strong>Personal Target:</strong>{" "}
                  {selectedStaffTarget > 0
                    ? selectedStaffTarget.toFixed(2)
                    : "—"}
                </div>
                <div>
                  <strong>Personal Achieved:</strong>{" "}
                  {selectedStaffRevenue.toFixed(2)}
                </div>
                <div>
                  <strong>% of Personal Target:</strong>{" "}
                  {selectedStaffPercent != null
                    ? `${selectedStaffPercent}%`
                    : "—"}
                </div>
                <div>
                  <strong>% of Shop Target:</strong>{" "}
                  {selectedStaffShareOfShop != null
                    ? `${selectedStaffShareOfShop}%`
                    : "—"}
                </div>
              </div>
            </div>
          )}

          {/* Staff table */}
          <table className="table">
            <thead>
              <tr>
                <th>Staff</th>
                <th>Role</th>
                <th>Service Revenue</th>
                <th>Item Revenue</th>
                <th>Total Revenue</th>
                <th>Personal Target</th>
                <th>% of Personal Target</th>
              </tr>
            </thead>
            <tbody>
              {staffList.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No staff performance data for this month.
                  </td>
                </tr>
              )}

              {staffList.map((s) => {
                const id =
                  s.staff_id ?? s.id ?? s.user_id ?? s.pk ?? s.username ?? "";
                const name =
                  s.username ||
                  s.name ||
                  s.full_name ||
                  `Staff ${s.staff_id ?? s.id ?? ""}`;
                const role = s.role || "-";
                const serviceRev = Number(s.service_revenue ?? 0);
                const itemRev = Number(s.item_revenue ?? 0);
                const totalRev = Number(s.total_revenue ?? 0);
                const target = Number(s.monthly_target ?? 0);
                const pct =
                  target > 0
                    ? Math.round((totalRev / target) * 100)
                    : null;

                const isSelected =
                  resolvedStaffId &&
                  String(id) === String(resolvedStaffId);

                return (
                  <tr
                    key={id}
                    style={
                      isSelected
                        ? { fontWeight: "bold", backgroundColor: "#f7f7f7" }
                        : undefined
                    }
                  >
                    <td>{name}</td>
                    <td>{role}</td>
                    <td>{serviceRev.toFixed(2)}</td>
                    <td>{itemRev.toFixed(2)}</td>
                    <td>{totalRev.toFixed(2)}</td>
                    <td>{target > 0 ? target.toFixed(2) : "—"}</td>
                    <td>{pct != null ? `${pct}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {!loading && !error && !metrics && (
        <div>No performance data yet.</div>
      )}
    </div>
  );
};

export default StaffPerformance;
